import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, euro, formatDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CaretDown, CaretRight, PencilSimple, Notebook, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { autoSend, channelsLabel } from "@/lib/messaging";

const MONTH_LABELS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const SOURCE_STYLE = {
  "Booking.com": { bg: "bg-blue-600 text-white shadow-sm",    dot: "#ffffff" },
  "Airbnb":      { bg: "bg-red-600 text-white shadow-sm",     dot: "#ffffff" },
  "MSB":         { bg: "bg-emerald-600 text-white shadow-sm", dot: "#ffffff" },
  "Website":     { bg: "bg-amber-600 text-white shadow-sm",   dot: "#ffffff" },
};
const YEARS_AVAILABLE = [2025, 2026];

function mealsLabel(row) {
  const b = row.breakfast_count || 0;
  const d = row.dinner_count || 0;
  if (b === 0 && d === 0) {
    // Legacy-Fallback für Buchungen ohne Zählung
    const parts = [];
    if (row.breakfast) parts.push("Frühstück");
    if (row.lunch)     parts.push("Mittag");
    if (row.dinner)    parts.push("Abend");
    return parts.length ? parts.join(" · ") : "—";
  }
  const parts = [];
  if (b > 0) parts.push(`${b}× Frühstück`);
  if (d > 0) parts.push(`${d}× Abend`);
  return parts.join(" · ");
}

/* Sort so that: current month → next month → then reverse chronological.
   Returns a key `(y, m)` sequence. */
function sortMonths(all, now) {
  const cur  = [now.getFullYear(), now.getMonth() + 1];
  const nxt  = now.getMonth() === 11 ? [now.getFullYear() + 1, 1] : [now.getFullYear(), now.getMonth() + 2];
  const key = (a) => a.year * 100 + a.month;
  const curK = cur[0]*100+cur[1], nxtK = nxt[0]*100+nxt[1];
  const priority = [];
  const rest = [];
  for (const m of all) {
    if (key(m) === curK)      priority[0] = m;
    else if (key(m) === nxtK) priority[1] = m;
    else                      rest.push(m);
  }
  rest.sort((a, b) => key(b) - key(a));       // absteigend chronologisch
  return [...priority.filter(Boolean), ...rest];
}

export default function MonthlyJournalPage() {
  const { user } = useAuth();
  const isAdmin  = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const roomFilter = searchParams.get("room") || "";
  const [period, setPeriod]   = useState("all");
  const [months, setMonths]   = useState([]);
  const [rooms, setRooms]     = useState([]);
  const [openMap, setOpenMap] = useState({});
  const [editRow, setEditRow] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearRoomFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("room");
    setSearchParams(next, { replace: true });
  };
  const roomLabel = useMemo(
    () => rooms.find(r => r.id === roomFilter)?.name || roomFilter,
    [rooms, roomFilter]
  );

  useEffect(() => {
    api.get("/rooms").then(r => setRooms(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const yearsToFetch = period === "all" ? YEARS_AVAILABLE : [Number(period)];
      const responses = await Promise.all(
        yearsToFetch.map(y => api.get(`/journal/monthly?year=${y}`))
      );
      // Zusammenführen und mit year taggen
      const flat = [];
      for (const r of responses) {
        for (const m of r.data.months) flat.push({ ...m, year: r.data.year });
      }
      // Optional: nach Zimmer filtern
      const filteredFlat = roomFilter
        ? flat.map(m => {
            const rows = (m.rows || []).filter(row => row.room_id === roomFilter);
            const rowSum = rows.reduce((s, r) => s + (r.gross || 0), 0);
            return {
              ...m,
              rows,
              row_count: rows.length,
              totals: { ...m.totals, uebernachtung: rowSum, gesamt: rowSum, verpflegung: 0, trinkgeld: 0 },
            };
          }).filter(m => m.row_count > 0)
        : flat;
      const now = new Date();
      const sorted = sortMonths(filteredFlat, now);
      setMonths(sorted);
      // Nur aktueller + Folgemonat aufgeklappt
      const curKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
      const nextD  = now.getMonth() === 11
        ? `${now.getFullYear() + 1}-1`
        : `${now.getFullYear()}-${now.getMonth() + 2}`;
      const open = {};
      for (const m of sorted) {
        const k = `${m.year}-${m.month}`;
        if (k === curKey || k === nextD) open[k] = true;
      }
      setOpenMap(open);
    } catch (e) {
      toast.error("Journal konnte nicht geladen werden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, roomFilter]);

  const toggle = (key) => setOpenMap(o => ({ ...o, [key]: !o[key] }));

  // Aggregat für Kopfzeilen-Summary
  const summary = useMemo(() => {
    const s = { rows: 0, uebernachtung: 0, verpflegung: 0, trinkgeld: 0, gesamt: 0 };
    for (const m of months) {
      s.rows          += m.row_count;
      s.uebernachtung += m.totals.uebernachtung || 0;
      s.verpflegung   += m.totals.verpflegung || 0;
      s.trinkgeld     += m.totals.trinkgeld || 0;
      s.gesamt        += m.totals.gesamt || 0;
    }
    return s;
  }, [months]);

  return (
    <div className="space-y-6" data-testid="monthly-journal-page">
      {/* Sticky Header mit Filter */}
      <div className="sticky top-0 z-20 -mx-6 px-6 py-4 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-primary">
              <Notebook size={22} weight="fill" color="#fff"/>
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>
                Monats-Journal
              </h1>
              <p className="text-muted-foreground text-sm">
                {roomFilter
                  ? <>Gefiltert nach Zimmer: <strong>{roomLabel}</strong></>
                  : "Tag für Tag alle Buchungen · aktueller und Folgemonat sind aufgeklappt"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {roomFilter && (
              <Button variant="outline" size="sm" onClick={clearRoomFilter}
                      className="rounded-xl" data-testid="journal-clear-room-filter">
                Zimmerfilter aufheben ×
              </Button>
            )}
            <div className="inline-flex rounded-2xl border border-border p-1 bg-muted/30" data-testid="journal-period-switcher">
            {[["2025","2025"], ["2026","2026"], ["all","Gesamt"]].map(([val, label]) => (
              <button key={val}
                      onClick={()=>setPeriod(val)}
                      className={`px-4 py-1.5 text-sm rounded-xl transition ${period === val ? "bg-white text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid={`journal-period-${val}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      </div>

      {loading && months.length === 0 && (
        <div className="text-muted-foreground text-sm">Lade Buchungen…</div>
      )}

      <div className="space-y-3">
        {months.map(m => {
          const key = `${m.year}-${m.month}`;
          const isOpen = !!openMap[key];
          const t = m.totals;
          return (
            <div key={key} className="rounded-2xl border border-border overflow-hidden"
                 data-testid={`journal-month-${m.year}-${m.month}`}>
              <button
                onClick={()=>toggle(key)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition text-left"
                data-testid={`journal-toggle-${m.year}-${m.month}`}>
                <div className="flex items-center gap-2">
                  {isOpen ? <CaretDown size={14} weight="bold"/> : <CaretRight size={14} weight="bold"/>}
                  <span className="font-semibold">{MONTH_LABELS[m.month-1]} {m.year}</span>
                  <span className="text-xs text-muted-foreground">
                    {m.row_count === 0 ? "keine Buchung" : `${m.row_count} Buchung${m.row_count === 1 ? "" : "en"}`}
                  </span>
                </div>
                <div className="text-sm font-semibold">{euro(t.gesamt)}</div>
              </button>

              {isOpen && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[820px] text-sm">
                    <thead className="bg-muted/20">
                      <tr className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        <th className="text-left px-4 py-2 font-semibold">Datum</th>
                        <th className="text-left px-4 py-2 font-semibold">Name</th>
                        <th className="text-center px-3 py-2 font-semibold">Pers.</th>
                        <th className="text-left px-4 py-2 font-semibold">Verpflegung</th>
                        <th className="text-left px-4 py-2 font-semibold">Quelle</th>
                        <th className="text-right px-4 py-2 font-semibold">Betrag</th>
                        <th className="text-center px-3 py-2 font-semibold w-24">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.rows.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground text-xs italic">
                            Keine Buchungen erfasst.
                          </td>
                        </tr>
                      )}
                      {m.rows.map(row => {
                        const src = SOURCE_STYLE[row.source] || SOURCE_STYLE["MSB"];
                        return (
                          <tr key={row.id}
                              onClick={()=>isAdmin && setEditRow(row)}
                              className={`border-t border-border ${isAdmin ? "hover:bg-primary/5 cursor-pointer" : ""}`}
                              data-testid={`journal-row-${row.id}`}>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="font-medium">{formatDate(row.date)}</div>
                              {row.nights > 0 && (
                                <div className="text-[11px] text-muted-foreground">{row.nights} N. · bis {formatDate(row.check_out)}</div>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <div className="font-medium">{row.name}</div>
                              {row.room_name && <div className="text-[11px] text-muted-foreground">{row.room_name}</div>}
                            </td>
                            <td className="px-3 py-2 text-center font-semibold">{row.persons}</td>
                            <td className="px-4 py-2 text-xs">{mealsLabel(row)}</td>
                            <td className="px-4 py-2">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${src.bg}`}
                                    data-testid={`journal-source-${row.id}`}>
                                {row.source}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right font-semibold whitespace-nowrap">
                              {row.gross != null ? euro(row.gross) : <span className="text-xs text-muted-foreground italic">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={(e)=>{e.stopPropagation(); const today = new Date().toISOString().slice(0,10); autoSend(row, row.check_out <= today ? "thankyou" : "welcome");}}
                                        className={`p-1.5 rounded-lg transition ${(row.guest_phone || row.guest_email) ? "text-primary hover:bg-primary/10" : "text-muted-foreground/40 cursor-not-allowed"}`}
                                        title={(row.guest_phone || row.guest_email) ? `Auto-Versand · ${channelsLabel(row)}` : "Kein Kontakt hinterlegt"}
                                        disabled={!row.guest_phone && !row.guest_email}
                                        data-testid={`journal-send-${row.id}`}>
                                  <PaperPlaneTilt size={14} weight="fill"/>
                                </button>
                                {isAdmin && (
                                  <button onClick={(e)=>{e.stopPropagation(); setEditRow(row);}}
                                          className="p-1.5 rounded-lg opacity-60 hover:opacity-100 hover:bg-muted transition"
                                          data-testid={`journal-edit-${row.id}`}
                                          title="Bearbeiten">
                                    <PencilSimple size={14} weight="bold"/>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editRow && (
        <JournalEditDialog row={editRow}
                           onClose={()=>setEditRow(null)}
                           onSaved={()=>{ setEditRow(null); load(); }}/>
      )}
    </div>
  );
}

function SummaryItem({ label, value, strong }) {
  return (
    <div>
      <div className="uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">{label}</div>
      <div className={`mt-0.5 ${strong ? "text-base font-bold text-primary" : "text-sm font-semibold"}`}>{value}</div>
    </div>
  );
}

function JournalEditDialog({ row, onClose, onSaved }) {
  const [name, setName]           = useState(row.name);
  const [checkIn, setCheckIn]     = useState(row.date);
  const [checkOut, setCheckOut]   = useState(row.check_out);
  const [persons, setPersons]     = useState(row.persons);
  const [source, setSource]       = useState(row.source);
  const [gross, setGross]         = useState(row.gross ?? "");
  const [breakfast, setBreakfast] = useState(!!row.breakfast);
  const [dinner, setDinner]       = useState(!!row.dinner);
  const [saving, setSaving]       = useState(false);

  const save = async () => {
    const clean = (name || "").trim().replace(/\s+/g, " ");
    if (!clean) { toast.error("Name eintragen"); return; }
    if (!checkIn || !checkOut) { toast.error("Zeitraum prüfen"); return; }
    if (new Date(checkOut) <= new Date(checkIn)) { toast.error("Abreise muss nach Anreise liegen"); return; }
    const parts = clean.split(" ");
    setSaving(true);
    try {
      await api.patch(`/bookings/${row.id}/edit`, {
        first_name: parts[0],
        last_name:  parts.slice(1).join(" "),
        check_in:   checkIn,
        check_out:  checkOut,
        gross:      gross === "" ? null : Number(gross),
        guests:     Number(persons),
        source, breakfast, dinner,
      });
      toast.success("Zeile aktualisiert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v)=>{ if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="journal-edit-dialog">
        <DialogHeader><DialogTitle>Buchungszeile bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Gast (Vor- und Nachname)</Label>
            <Input value={name} onChange={e=>setName(e.target.value)}
                   className="rounded-xl mt-1.5" data-testid="journal-edit-name"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Anreise</Label><Input type="date" value={checkIn} onChange={e=>setCheckIn(e.target.value)} className="rounded-xl mt-1.5" data-testid="journal-edit-checkin"/></div>
            <div><Label>Abreise</Label><Input type="date" value={checkOut} onChange={e=>setCheckOut(e.target.value)} className="rounded-xl mt-1.5" data-testid="journal-edit-checkout"/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Personen</Label><Input type="number" min="1" max="20" value={persons} onChange={e=>setPersons(+e.target.value || 1)} className="rounded-xl mt-1.5" data-testid="journal-edit-persons"/></div>
            <div><Label>Betrag (€)</Label><Input type="number" step="0.01" value={gross} onChange={e=>setGross(e.target.value)} className="rounded-xl mt-1.5" data-testid="journal-edit-gross"/></div>
          </div>
          <div>
            <Label>Buchungsquelle</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="journal-edit-source"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="Booking.com">Booking.com</SelectItem>
                <SelectItem value="Airbnb">Airbnb</SelectItem>
                <SelectItem value="MSB">MSB (Direkt)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={breakfast} onCheckedChange={v=>setBreakfast(!!v)} data-testid="journal-edit-breakfast"/>
              Frühstück
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={dinner} onCheckedChange={v=>setDinner(!!v)} data-testid="journal-edit-dinner"/>
              Abendessen
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-primary" data-testid="journal-edit-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
